/*! @wharfkit/transact-plugin-resource-provider v4.0.0-rc7 | BSD-3-Clause | Copyright (c) 2022 Greymass Inc. | https://github.com/wharfkit/js */
import { AbstractTransactPlugin, Asset, Canceled, Name, Serializer, Signature, SigningRequest, Struct, TransactHookTypes, Transaction, UInt32 } from "@wharfkit/session";
import { Resources } from "@wharfkit/resources";
//#endregion
//#region src/translations/index.ts
var translations_default = {
	en: {
		timeout: "The offer from the resource provider has expired.",
		"will-continue": "The transaction will continue without the resource provider.",
		fee: {
			"title": "Accept Transaction Fee?",
			"body": "Additional resources ({{resource}}) are required for your account to perform this transaction. Would you like to automatically purchase these resources and proceed?",
			"cost": "Cost of {{resource}}"
		},
		rejected: {
			"no-fees": "A resource provider offered to cover this transaction for a fee, but fee-based transactions are disabled by the configuration using `allowFees = false`.",
			"original-modified": "The original transaction returned by the resource provider has been modified too much. Continuing without the resource provider",
			"max-fee": "The fee requested by the resource provider is unusually high and has been rejected."
		}
	},
	ko: {
		timeout: "리소스 공급자의 제안이 만료되었습니다.",
		"will-continue": "트랜잭션은 리소스 공급자 없이 계속됩니다.",
		fee: {
			"title": "거래 수수료를 수락하시겠습니까?",
			"body": "계정에서 이 거래를 수행하려면 추가 리소스({{resource}})가 필요합니다. 이러한 리소스를 자동으로 구매하고 계속 진행하시겠습니까?",
			"cost": "{{resource}}의 비용"
		},
		rejected: {
			"no-fees": "리소스 공급자가 이 트랜잭션을 유료로 처리하도록 제안했지만 수수료 기반 트랜잭션은 'allowFees = false'를 사용하는 구성에 의해 비활성화됩니다.",
			"original-modified": "리소스 공급자가 반환한 원래 트랜잭션이 너무 많이 수정되었습니다. 리소스 공급자 없이 계속",
			"max-fee": "리소스 공급자가 요청한 수수료가 비정상적으로 높아 거부되었습니다."
		}
	},
	"zh-Hans": {
		timeout: "来自资源提供商的产品/服务已过期。",
		"will-continue": "交易将在不使用资源提供商的情况下继续。",
		fee: {
			"title": "接受交易费用？",
			"body": "您的账户需要其他资源 （{{resource}}） 才能执行此交易。是否要自动购买这些资源并继续？",
			"cost": "{{resource}} 的成本"
		},
		rejected: {
			"no-fees": "资源提供商提议以一定费用为条件提供此交易所需的资源，但配置项“allowFee = false”禁止了接受这种提议。",
			"original-modified": "资源提供商返回的原始交易修改过多。将在不使用资源提供商的情况下继续",
			"max-fee": "资源提供商提议的费用过高，已拒绝。"
		}
	},
	"zh-Hant": {
		timeout: "來自資源提供商的產品/服務已過期。",
		"will-continue": "交易將在不使用資源提供商的情況下繼續。",
		fee: {
			"title": "接受交易費用？",
			"body": "您的賬戶需要其他資源 （{{resource}}） 才能執行此交易。是否要自動購買這些資源並繼續？",
			"cost": "{{resource}} 的成本"
		},
		rejected: {
			"no-fees": "資源提供商提議以一定費用為條件提供此交易所需的資源，但配置項“allowFee = false”禁止了接受這種提議。",
			"original-modified": "資源提供商返回的原始交易修改過多。將在不使用資源提供商的情況下繼續",
			"max-fee": "資源提供商提議的費用過高，已拒絕。"
		}
	}
};
//#endregion
//#region src/utils.ts
function hasOriginalActions(original, modified) {
	return original.actions.every((originalAction) => {
		return modified.actions.some((modifiedAction) => {
			const matchesOriginalContractAccount = originalAction.account.equals(modifiedAction.account);
			const matchesOriginalContractAction = originalAction.name.equals(modifiedAction.name);
			const matchesOriginalAuthorization = originalAction.authorization.length === modifiedAction.authorization.length && originalAction.authorization[0].actor.equals(modifiedAction.authorization[0].actor);
			const matchesOriginalActionData = originalAction.data.equals(modifiedAction.data);
			return matchesOriginalContractAccount && matchesOriginalContractAction && matchesOriginalAuthorization && matchesOriginalActionData;
		});
	});
}
function getNewActions(original, modified) {
	return modified.actions.filter((modifiedAction) => {
		return original.actions.every((originalAction) => {
			const matchesOriginalContractAccount = originalAction.account.equals(modifiedAction.account);
			const matchesOriginalContractAction = originalAction.name.equals(modifiedAction.name);
			const matchesOriginalAuthorization = originalAction.authorization.length === modifiedAction.authorization.length && originalAction.authorization[0].actor.equals(modifiedAction.authorization[0].actor);
			const matchesOriginalActionData = originalAction.data.equals(modifiedAction.data);
			return !(matchesOriginalContractAccount && matchesOriginalContractAction && matchesOriginalAuthorization && matchesOriginalActionData);
		});
	});
}
//#endregion
//#region \0@oxc-project+runtime@0.147.0/helpers/esm/decorate.js
function __decorate(decorators, target, key, desc) {
	var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
	if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
	else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
	return c > 3 && r && Object.defineProperty(target, key, r), r;
}
//#endregion
//#region src/index.ts
let Transfer = class Transfer extends Struct {};
__decorate([Struct.field(Name)], Transfer.prototype, "from", void 0);
__decorate([Struct.field(Name)], Transfer.prototype, "to", void 0);
__decorate([Struct.field(Asset)], Transfer.prototype, "quantity", void 0);
__decorate([Struct.field("string")], Transfer.prototype, "memo", void 0);
Transfer = __decorate([Struct.type("transfer")], Transfer);
let BuyRAMBytes = class BuyRAMBytes extends Struct {};
__decorate([Struct.field(Name)], BuyRAMBytes.prototype, "payer", void 0);
__decorate([Struct.field(Name)], BuyRAMBytes.prototype, "receiver", void 0);
__decorate([Struct.field(UInt32)], BuyRAMBytes.prototype, "bytes", void 0);
BuyRAMBytes = __decorate([Struct.type("buyrambytes")], BuyRAMBytes);
const defaultOptions = { endpoints: {
	aca376f206b8fc25a6ed44dbdc66547c36c6c33e3a119ffbeaef943642f0e906: "https://eos.greymass.com",
	"73e4385a2708e6d7048834fbc1079f2fabb17b3c125b146af438971e90716c4d": "https://jungle4.greymass.com",
	"4667b205c6838ef70ff7988f6e8257e8be0e1284a2f59699054a018f743b1d11": "https://telos.greymass.com",
	"1064487b3cd1a897ce03ae5b6a865651747e2e152090f99c1d19d44e01aea5a4": "https://wax.greymass.com"
} };
var TransactPluginResourceProvider = class extends AbstractTransactPlugin {
	constructor(options) {
		super();
		this.id = "transact-plugin-resource-provider";
		this.translations = translations_default;
		this.allowFees = true;
		this.endpoints = defaultOptions.endpoints;
		if (options) {
			if (options.endpoints) this.endpoints = options.endpoints;
			if (typeof options.allowFees !== "undefined") this.allowFees = options.allowFees;
			if (typeof options.maxFee !== "undefined") this.maxFee = Asset.from(options.maxFee);
		}
	}
	register(context) {
		context.addHook(TransactHookTypes.beforeSign, async (request, context) => {
			return this.request(request, context);
		});
	}
	getEndpoint(chain) {
		return this.endpoints[String(chain.id)];
	}
	async request(request, context) {
		let t = (key, options) => options.default;
		if (context.ui) t = context.ui.getTranslate(this.id);
		const endpoint = this.getEndpoint(context.chain);
		if (!endpoint) return { request };
		let modifiedRequest;
		const abis = await request.fetchAbis(context.abiCache);
		if (request.requiresTapos()) {
			const header = (await context.client.v1.chain.get_info()).getTransactionHeader(120);
			const modifiedArgs = { transaction: request.resolveTransaction(abis, context.permissionLevel, header) };
			modifiedArgs.chainId = request.getChainId();
			if (request.isMultiChain()) {
				const ids = request.getChainIds();
				if (ids) modifiedArgs.chainIds = ids;
			}
			modifiedRequest = await SigningRequest.create(modifiedArgs, context.esrOptions);
		} else modifiedRequest = await SigningRequest.create({ transaction: request.resolveTransaction(abis, context.permissionLevel) }, context.esrOptions);
		this.validateRequest(modifiedRequest, context);
		const url = `${endpoint}/v1/resource_provider/request_transaction`;
		let response;
		let json;
		try {
			response = await context.fetch(url, {
				method: "POST",
				body: JSON.stringify({
					request: modifiedRequest,
					signer: context.permissionLevel
				})
			});
			json = await response.json();
		} catch {
			return { request };
		}
		if (response.status === 400 || ![200, 402].includes(response.status)) return { request };
		if (response.status === 402) {
			if (!this.allowFees) {
				if (context.ui) context.ui.status(`${t("rejected.no-fees", { default: "A resource provider offered to cover this transaction for a fee, but fee-based transactions are disabled by the configuration using `allowFees = false`." })} ${t("will-continue", { default: "The transaction will continue without the resource provider." })}`);
				return { request };
			}
		}
		const modifiedTransaction = this.getModifiedTransaction(json);
		if (!hasOriginalActions(modifiedRequest.getRawTransaction(), modifiedTransaction)) {
			if (context.ui) context.ui.status(`${t("rejected.original-modified", { default: "The original transaction returned by the resource provider has been modified too much. Continuing without the resource provider" })} ${t("will-continue", { default: "The transaction will continue without the resource provider." })}`);
			return { request };
		}
		const addedActions = getNewActions(modifiedRequest.getRawTransaction(), modifiedTransaction);
		for (const action of addedActions) {
			const isNoop = action.name.equals("noop");
			const isTransfer = action.name.equals("transfer") && (action.account.equals("eosio.token") || action.account.equals("core.vaulta"));
			const isBuyRAMBytes = action.name.equals("buyrambytes") && (action.account.equals("eosio") || action.account.equals("core.vaulta"));
			if (!isNoop && !isTransfer && !isBuyRAMBytes) {
				if (context.ui) context.ui.status(`${t("rejected.unexpected-action", { default: "The resource provider added an unexpected action to the transaction." })} ${t("will-continue", { default: "The transaction will continue without the resource provider." })}`);
				return { request };
			}
			if (isBuyRAMBytes) {
				if (!Serializer.decode({
					data: action.data,
					type: BuyRAMBytes
				}).receiver.equals(context.permissionLevel.actor)) {
					if (context.ui) context.ui.status(`${t("rejected.invalid-buyrambytes", { default: "The resource provider added a RAM purchase for an account other than the requesting account." })} ${t("will-continue", { default: "The transaction will continue without the resource provider." })}`);
					return { request };
				}
			}
		}
		const transferFees = addedActions.filter((action) => action.account.equals("eosio.token") && action.name.equals("transfer") || action.account.equals("core.vaulta") && action.name.equals("transfer")).map((action) => {
			return Serializer.decode({
				data: action.data,
				type: Transfer
			}).quantity;
		});
		const ramActions = addedActions.filter((action) => action.name.equals("buyrambytes") && (action.account.equals("eosio") || action.account.equals("core.vaulta")));
		const ramFees = [];
		if (ramActions.length > 0) {
			const ramState = await new Resources({ api: context.client }).v1.ram.get_state();
			for (const action of ramActions) {
				const decoded = Serializer.decode({
					data: action.data,
					type: BuyRAMBytes
				});
				ramFees.push(ramState.price_per(Number(decoded.bytes)));
			}
		}
		const allFees = [...transferFees, ...ramFees];
		const addedFees = allFees.reduce((total, fee) => {
			total.units.add(fee.units);
			return total;
		}, Asset.fromUnits(0, allFees.length > 0 ? allFees[0].symbol : "4,TOKEN"));
		if (this.maxFee) {
			if (addedFees.units > this.maxFee.units) {
				if (context.ui) context.ui.status(`${t("rejected.max-fee", { default: "The fee requested by the resource provider is unusually high and has been rejected." })} ${t("will-continue", { default: "The transaction will continue without the resource provider." })}`);
				return { request };
			}
		}
		await this.validateResponseData(json);
		const modified = await this.createRequest(json, context);
		if (context.ui && addedFees.value > 0) {
			const resourceTypes = [];
			if (json.data.costs) {
				const { cpu, net, ram } = json.data.costs;
				if (Asset.from(cpu).value > 0) resourceTypes.push("CPU");
				if (Asset.from(net).value > 0) resourceTypes.push("NET");
				if (Asset.from(ram).value > 0) resourceTypes.push("RAM");
			} else resourceTypes.push("Unknown");
			const prompt = context.ui.prompt({
				title: t("fee.title", { default: "Accept Transaction Fee?" }),
				body: t("fee.body", {
					default: "Additional resources ({{resource}}) are required for your account to perform this transaction. Would you like to automatically purchase these resources and proceed?",
					resource: String(resourceTypes.join("/"))
				}),
				elements: [{
					type: "asset",
					data: {
						label: t("fee.cost", {
							default: "Cost of {{resource}}",
							resource: String(resourceTypes.join("/"))
						}),
						value: addedFees
					}
				}, { type: "accept" }]
			});
			const timer = setTimeout(() => {
				prompt.cancel(t("timeout", { default: "The offer from the resource provider has expired." }));
			}, 12e4);
			return prompt.then(async () => {
				return new Promise((r) => r({
					request: modified,
					signatures: json.data.signatures.map((sig) => Signature.from(sig))
				}));
			}).catch((e) => {
				if (e instanceof Canceled) throw e;
				return new Promise((r) => r({ request }));
			}).finally(() => {
				clearTimeout(timer);
			});
		}
		return new Promise((r) => r({
			request: modified,
			signatures: json.data.signatures.map((sig) => Signature.from(sig))
		}));
	}
	getModifiedTransaction(json) {
		switch (json.data.request[0]) {
			case "action": throw new Error("A resource provider providing an \"action\" is not supported.");
			case "actions": throw new Error("A resource provider providing \"actions\" is not supported.");
			case "transaction": return Transaction.from(json.data.request[1]);
		}
		throw new Error("Invalid request type provided by resource provider.");
	}
	async createRequest(response, context) {
		const request = await context.createRequest(response.data.request[1]);
		if (response.code === 402 && response.data.fee) request.setInfoKey("txfee", Asset.from(response.data.fee));
		if (response.data.costs) {
			request.setInfoKey("txfeecpu", response.data.costs.cpu);
			request.setInfoKey("txfeenet", response.data.costs.net);
			request.setInfoKey("txfeeram", response.data.costs.ram);
		}
		return request;
	}
	validateRequest(request, context) {
		if (!request.getRawActions()[0].authorization[0].actor.equals(context.permissionLevel.actor)) throw new Error("The first authorizer of the transaction does not match this session.");
	}
	async validateResponseData(response) {
		if (!response) throw new Error("Resource provider did not respond to the request.");
		if (response.code === 402 && !response.data.fee) throw new Error("Resource provider returned a response indicating required payment, but provided no fee amount.");
		if (!response.data.signatures || !response.data.signatures[0]) throw new Error("Resource provider did not return a signature.");
	}
};
//#endregion
export { BuyRAMBytes, TransactPluginResourceProvider, Transfer, defaultOptions };

//# sourceMappingURL=transact-plugin-resource-provider.m.js.map